import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Pencil, Trash2, Plus, Search } from "lucide-react";
import { useCollection } from "@/hooks/useCollection";
import { inDemo } from "@/lib/api";
import { useBach } from "@/context/BachContext";
import { ImageField, ImageThumb } from "@/components/ImageField";
import { formatMoney } from "@/lib/format";
import { toast } from "sonner";

const PAYMENT_BADGE = {
  Paid: "bg-emerald-100 text-emerald-800 border-emerald-200",
  Partial: "bg-amber-100 text-amber-800 border-amber-200",
  Unpaid: "bg-[#F9ECE8] text-[#6B1D2F] border-[#ECCDC5]",
};

function Field({ field, value, onChange }) {
  const common = { id: field.key, "data-testid": `bach-field-${field.key}` };
  if (field.type === "image")
    return <ImageField value={value} onChange={onChange} testId={`bach-field-${field.key}`} />;
  if (field.type === "select")
    return (
      <Select value={value || ""} onValueChange={onChange}>
        <SelectTrigger {...common} className="rounded-xl border-[#EFE3DE] focus:ring-[#D8BD9E]"><SelectValue placeholder={`Select ${field.label.toLowerCase()}`} /></SelectTrigger>
        <SelectContent>{field.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
      </Select>
    );
  if (field.type === "checkbox")
    return <div className="flex items-center gap-2 pt-1"><Checkbox {...common} checked={!!value} onCheckedChange={onChange} className="border-[#D8BD9E] data-[state=checked]:bg-[#6B1D2F] data-[state=checked]:border-[#6B1D2F]" /><span className="text-sm text-[#7A686C]">Yes</span></div>;
  if (field.type === "textarea")
    return <Textarea {...common} value={value || ""} onChange={(e) => onChange(e.target.value)} className="rounded-xl border-[#EFE3DE] focus-visible:ring-[#D8BD9E]" rows={3} />;
  return <Input {...common} type={field.type === "number" ? "number" : field.type === "date" ? "date" : "text"} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="rounded-xl border-[#EFE3DE] focus-visible:ring-[#D8BD9E]" />;
}

export default function BachManager({ config }) {
  const cur = useBach()?.profile?.currency || "AUD";
  const { entity, title, subtitle, fields, columns, variant, doneKey, labelKey, summary, image } = config;
  const { items, loading, create, update, remove } = useCollection(entity, "/bach");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [query, setQuery] = useState("");

  const openNew = () => { setEditing(null); setForm({}); setOpen(true); };
  const openEdit = (item) => { setEditing(item); setForm({ ...item }); setOpen(true); };

  const onDelete = (id) => { if (inDemo()) { window.dispatchEvent(new CustomEvent("demo-locked")); return; } remove(id); toast.success("Deleted"); };

  const submit = async () => {
    if (inDemo()) { window.dispatchEvent(new CustomEvent("demo-locked")); setOpen(false); return; }
    const payload = {};
    fields.forEach((f) => {
      let v = form[f.key];
      if (f.type === "number") v = v === "" || v == null ? 0 : Number(v);
      if (f.type === "checkbox") v = !!v;
      payload[f.key] = v ?? (f.type === "checkbox" ? false : "");
    });
    if (variant === "checklist") payload[doneKey] = editing ? !!editing[doneKey] : false;
    if (editing) { await update(editing.id, payload); toast.success("Updated"); }
    else { await create(payload); toast.success("Added"); }
    setOpen(false);
  };

  const filtered = items.filter((it) => !query.trim() || Object.values(it).some((v) => String(v).toLowerCase().includes(query.toLowerCase())));
  const cell = (c, it) => {
    let v = c.compute ? c.compute(it) : it[c.key];
    if (c.money) return formatMoney(Number(v) || 0, cur);
    if (c.bool) return v ? "Yes" : "—";
    if (c.badge === "payment") return <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold border ${PAYMENT_BADGE[v] || PAYMENT_BADGE.Unpaid}`}>{v || "Unpaid"}</span>;
    return String(v ?? "") || "—";
  };

  const summ = summary && items.length ? summary(items) : null;

  const header = (
    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
      <div>
        <h1 className="font-serif text-4xl sm:text-5xl tracking-tight text-[#2A181C] leading-tight">{title}</h1>
        {subtitle && <p className="text-[#7A686C] mt-2">{subtitle}</p>}
      </div>
      <Button onClick={openNew} data-testid={`bach-add-${entity}`} className="rounded-full bg-[#6B1D2F] hover:bg-[#541423] text-white shrink-0"><Plus className="h-4 w-4 mr-1.5" /> Add</Button>
    </div>
  );

  if (loading) return <div className="animate-pulse space-y-4"><div className="h-10 w-56 bg-[#F5EFEA] rounded-lg" /><div className="h-64 bg-[#F5EFEA] rounded-2xl" /></div>;

  return (
    <div>
      {header}

      {summ && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-6 bg-[#F9ECE8] border border-[#ECCDC5] rounded-2xl mb-6" data-testid={`bach-summary-${entity}`}>
          {summ.map((s) => (
            <div key={s.label}>
              <div className="text-xs uppercase tracking-wider text-[#7A686C]">{s.label}</div>
              <div className="text-2xl font-serif text-[#6B1D2F] mt-1 tnum">{s.money ? formatMoney(s.value, cur) : s.value}</div>
            </div>
          ))}
        </div>
      )}

      {items.length > 0 && (
        <div className="relative max-w-xs mb-4">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#7A686C]" />
          <Input placeholder="Search…" value={query} onChange={(e) => setQuery(e.target.value)} className="pl-9 rounded-full border-[#EFE3DE] focus-visible:ring-[#D8BD9E]" data-testid={`bach-search-${entity}`} />
        </div>
      )}

      {items.length === 0 ? (
        <div className="flex flex-col items-center justify-center p-12 text-center bg-[#FAF8F5] border border-dashed border-[#ECCDC5] rounded-2xl" data-testid={`bach-empty-${entity}`}>
          {config.icon && <config.icon className="w-12 h-12 text-[#6B1D2F]/60 mb-3" strokeWidth={1.5} />}
          <h3 className="font-serif text-xl text-[#2A181C] font-semibold mb-1">Nothing here yet</h3>
          <p className="text-sm text-[#7A686C] max-w-sm mb-4">{subtitle}</p>
          <Button onClick={openNew} className="rounded-full bg-[#6B1D2F] hover:bg-[#541423] text-white" data-testid={`bach-empty-add-${entity}`}><Plus className="h-4 w-4 mr-1.5" /> Add your first</Button>
        </div>
      ) : variant === "checklist" ? (
        <div className="space-y-2" data-testid={`bach-list-${entity}`}>
          {filtered.map((it) => (
            <div key={it.id} className="bg-white border border-[#EFE3DE] rounded-xl p-4 flex items-center justify-between hover:border-[#ECCDC5] transition-all" data-testid={`bach-row-${entity}`}>
              <label className="flex items-center gap-3 min-w-0 cursor-pointer">
                <Checkbox checked={!!it[doneKey]} onCheckedChange={(v) => { if (inDemo()) { window.dispatchEvent(new CustomEvent("demo-locked")); return; } update(it.id, { [doneKey]: !!v }); }} className="h-5 w-5 border-[#D8BD9E] data-[state=checked]:bg-[#6B1D2F] data-[state=checked]:border-[#6B1D2F]" data-testid={`bach-check-${entity}`} />
                <span className="min-w-0">
                  <span className={`block truncate ${it[doneKey] ? "line-through text-[#7A686C]" : "text-[#2A181C]"}`}>{it[labelKey] || "Untitled"}</span>
                  {(it.category || it.due_date || it.assignee) && <span className="text-xs text-[#7A686C]">{[it.category, it.due_date, it.assignee].filter(Boolean).join(" · ")}</span>}
                </span>
              </label>
              <div className="flex items-center gap-1 shrink-0">
                <Button variant="ghost" size="icon" className="h-8 w-8 text-[#7A686C]" onClick={() => openEdit(it)} data-testid={`bach-edit-${entity}`}><Pencil className="h-4 w-4" /></Button>
                <DeleteBtn entity={entity} onConfirm={() => onDelete(it.id)} />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="rounded-2xl border border-[#EFE3DE] bg-white overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-[#2A181C]">
              <thead><tr className="bg-[#F5EFEA] text-left">
                {image && <th className="px-4 py-3 w-16"></th>}
                {columns.map((c) => <th key={c.key} className="px-4 py-3 font-semibold uppercase text-[11px] tracking-wider text-[#7A686C] whitespace-nowrap">{c.label}</th>)}
                <th className="px-4 py-3 w-20"></th>
              </tr></thead>
              <tbody>
                {filtered.map((it) => (
                  <tr key={it.id} className="border-t border-[#EFE3DE] hover:bg-[#FAF8F5] transition-colors" data-testid={`bach-row-${entity}`}>
                    {image && <td className="px-4 py-2">{it[image] ? <ImageThumb value={it[image]} /> : <div className="h-10 w-14 rounded-lg bg-[#F5EFEA]" />}</td>}
                    {columns.map((c) => <td key={c.key} className="px-4 py-3 align-top">{cell(c, it)}</td>)}
                    <td className="px-4 py-3"><div className="flex items-center gap-1 justify-end">
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-[#7A686C]" onClick={() => openEdit(it)} data-testid={`bach-edit-${entity}`}><Pencil className="h-4 w-4" /></Button>
                      <DeleteBtn entity={entity} onConfirm={() => onDelete(it.id)} />
                    </div></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-serif text-2xl text-[#2A181C]">{editing ? "Edit" : "Add"} {title.replace(/s$/, "")}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
            {fields.map((f) => (
              <div key={f.key} className={f.full ? "sm:col-span-2" : ""}>
                <Label htmlFor={f.key} className="text-xs text-[#7A686C]">{f.label}</Label>
                <div className="mt-1"><Field field={f} value={form[f.key]} onChange={(v) => setForm((p) => ({ ...p, [f.key]: v }))} /></div>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-full border-[#EFE3DE]">Cancel</Button>
            <Button onClick={submit} className="rounded-full bg-[#6B1D2F] hover:bg-[#541423] text-white" data-testid={`bach-save-${entity}`}>{editing ? "Save changes" : "Add"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DeleteBtn({ entity, onConfirm }) {
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild><Button variant="ghost" size="icon" className="h-8 w-8 text-[#6B1D2F]" data-testid={`bach-delete-${entity}`}><Trash2 className="h-4 w-4" /></Button></AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader><AlertDialogTitle>Delete this item?</AlertDialogTitle><AlertDialogDescription>This can't be undone.</AlertDialogDescription></AlertDialogHeader>
        <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={onConfirm} data-testid="bach-confirm-delete" className="bg-[#6B1D2F] hover:bg-[#541423]">Delete</AlertDialogAction></AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
